import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, Paper, Skeleton, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ConfirmationNumber as TicketIcon,
  ErrorOutline as ErrorOutlineIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  SouthWest as InboundIcon,
  NorthEast as OutboundIcon
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import EmptyState from '../../components/design/EmptyState';
import DataTable from '../../components/design/DataTable';
import { dashboardService } from '../../services/dashboardService';

function KpiCard({ title, value, icon, color }) {
  return (
    <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.9 }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
        {action || null}
      </Box>
      {children}
    </Paper>
  );
}

export default function WhatsAppDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const resp = await dashboardService.getOverview(14);
        setData(resp?.data || resp);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const kpis = data?.kpis || {};
  const ticketsByDay = Array.isArray(data?.charts?.ticketsByDay) ? data.charts.ticketsByDay : [];
  const messagesByHour = Array.isArray(data?.charts?.messagesByHour) ? data.charts.messagesByHour : [];
  const lastTickets = Array.isArray(data?.tables?.lastTickets) ? data.tables.lastTickets : [];
  const recentActivity = Array.isArray(data?.tables?.recentActivity) ? data.tables.recentActivity : [];
  const connections = Array.isArray(data?.tables?.connections) ? data.tables.connections : [];
  const ticketsByOperator = useMemo(
    () => (Array.isArray(data?.charts?.ticketsByOperator) ? data.charts.ticketsByOperator : []),
    [data?.charts?.ticketsByOperator]
  );

  const donutData = useMemo(() => {
    const list = Array.isArray(data?.charts?.ticketsByQueue) ? data.charts.ticketsByQueue : [];
    return list.slice(0, 8).map(r => ({ name: r.queue_name, value: Number(r.total || 0), color: r.queue_color || '#3b82f6' }));
  }, [data?.charts?.ticketsByQueue]);

  const operatorPie = useMemo(() => {
    if (!ticketsByOperator.length) return [];
    const total = ticketsByOperator.reduce((sum, r) => sum + Number(r.closedTicketsPeriod || 0), 0);
    if (!total) return [];
    const palette = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#eab308', '#14b8a6'];
    const top = ticketsByOperator.slice(0, 6).map((r, idx) => ({
      name: r.name,
      value: Number(r.closedTicketsPeriod || 0),
      color: palette[idx % palette.length]
    }));
    const rest = ticketsByOperator.slice(6).reduce((sum, r) => sum + Number(r.closedTicketsPeriod || 0), 0);
    if (rest > 0) top.push({ name: 'Outros', value: rest, color: 'rgba(229,231,235,0.35)' });
    return top;
  }, [ticketsByOperator]);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>Dashboard</Typography>
        {!loading && data ? (
          <Chip size="small" label="Dados em tempo real via API" variant="outlined" />
        ) : null}
      </Box>

      <Grid container spacing={2}>
        {loading ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <Grid key={idx} item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                <Skeleton variant="text" height={44} width="40%" />
                <Skeleton variant="text" height={20} width="70%" />
              </Paper>
            </Grid>
          ))
        ) : data ? (
          <>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Tickets abertos" value={kpis.openTickets ?? 0} icon={<TicketIcon />} color="#3b82f6" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Tickets pendentes" value={kpis.pendingTickets ?? 0} icon={<ScheduleIcon />} color="#f59e0b" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Tickets fechados" value={kpis.closedTickets ?? 0} icon={<CheckCircleIcon />} color="#22c55e" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Operadores online" value={kpis.operatorsOnline ?? 0} icon={<PeopleIcon />} color="#3b82f6" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="SLA vencidos" value={kpis.slaOverdue ?? 0} icon={<ErrorOutlineIcon />} color="#ef4444" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Mensagens recebidas hoje" value={kpis.messagesInboundToday ?? 0} icon={<InboundIcon />} color="#3b82f6" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Mensagens enviadas hoje" value={kpis.messagesOutboundToday ?? 0} icon={<OutboundIcon />} color="#22c55e" /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Falhas de envio hoje" value={kpis.sendFailuresToday ?? 0} icon={<ErrorOutlineIcon />} color="#ef4444" /></Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Paper>
              <EmptyState title="Falha ao carregar o dashboard" />
            </Paper>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} lg={7}>
          <SectionCard title="Evolução de tickets (dias)">
            {ticketsByDay.length ? (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ticketsByDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="day" tick={{ fill: 'rgba(229,231,235,0.85)', fontSize: 12 }} />
                    <YAxis tick={{ fill: 'rgba(229,231,235,0.85)', fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: '#1a1d23', border: '1px solid #2a2f38' }} />
                    <Legend />
                    <Line type="monotone" dataKey="created" name="Abertos" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="closed" name="Fechados" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pending" name="Pendentes" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState title="Sem dados no período" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard title="Tickets por fila (backlog)">
            {donutData.length ? (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip contentStyle={{ background: '#1a1d23', border: '1px solid #2a2f38' }} />
                    <Legend />
                    <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                      {donutData.map((e, i) => (<Cell key={i} fill={e.color} />))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState title="Sem dados" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <SectionCard title="Tickets por agente (período)">
            {ticketsByOperator.length ? (
              <DataTable
                columns={[
                  { key: 'name', label: 'Atendente' },
                  { key: 'openTickets', label: 'Abertos' },
                  { key: 'pendingTickets', label: 'Pendentes' },
                  { key: 'totalTickets', label: 'Carteira' },
                  { key: 'closedTicketsPeriod', label: 'Fechados', render: (v) => Number(v || 0) },
                  { key: 'closedPercentPeriod', label: '% fechados', render: (v) => `${Number(v || 0).toFixed(2)}%` }
                ]}
                rows={ticketsByOperator}
                getKey={(r) => r.id}
              />
            ) : (
              <EmptyState title="Sem dados de atendentes" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard title="Percentual de atendimento (fechados no período)">
            {operatorPie.length ? (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip contentStyle={{ background: '#1a1d23', border: '1px solid #2a2f38' }} />
                    <Legend />
                    <Pie data={operatorPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                      {operatorPie.map((e, i) => (<Cell key={i} fill={e.color} />))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState title="Sem fechamentos no período" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12}>
          <SectionCard title="Mensagens por hora (hoje)">
            {messagesByHour.length ? (
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={messagesByHour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="hour" tick={{ fill: 'rgba(229,231,235,0.85)', fontSize: 12 }} />
                    <YAxis tick={{ fill: 'rgba(229,231,235,0.85)', fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: '#1a1d23', border: '1px solid #2a2f38' }} />
                    <Legend />
                    <Bar dataKey="inbound" name="Recebidas" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="outbound" name="Enviadas" fill="#22c55e" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState title="Sem mensagens hoje" />
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} lg={6}>
          <SectionCard title="Últimos tickets atualizados">
            {lastTickets.length ? (
              <DataTable
                columns={[
                  { key: 'protocol', label: 'Protocolo' },
                  { key: 'status', label: 'Status' },
                  { key: 'lastMessage', label: 'Última mensagem', render: (v) => v ? String(v).slice(0, 80) : '—' },
                  { key: 'updatedAt', label: 'Atualizado', render: (v) => v ? new Date(v).toLocaleString() : '—' }
                ]}
                rows={lastTickets}
                getKey={(r) => r.id}
              />
            ) : (
              <EmptyState title="Sem tickets" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard title="Últimas atividades">
            {recentActivity.length ? (
              <DataTable
                columns={[
                  { key: 'created_at', label: 'Data', render: (v) => v ? new Date(v).toLocaleString() : '—' },
                  { key: 'user_name', label: 'Usuário', render: (v) => v || '—' },
                  { key: 'entity', label: 'Entidade' },
                  { key: 'action', label: 'Ação' }
                ]}
                rows={recentActivity}
                getKey={(r) => r.id}
              />
            ) : (
              <EmptyState title="Sem atividades" />
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12}>
          <SectionCard title="Conexões WhatsApp">
            {connections.length ? (
              <DataTable
                columns={[
                  { key: 'name', label: 'Conexão' },
                  { key: 'type', label: 'Tipo' },
                  { key: 'status', label: 'Status' },
                  { key: 'is_active', label: 'Ativa', render: (v) => v ? 'Sim' : 'Não' }
                ]}
                rows={connections}
                getKey={(r) => r.id}
              />
            ) : (
              <EmptyState title="Sem conexões" />
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
